/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Badge } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import { useChatContext } from '@/shared/contexts/chatContext';
import { useMemberContext } from '@/shared/contexts/memberContext';
import {
    countNotifByMemberId,
    countNotifByMemberIdWithNotNullAdminId,
} from '@/apis/lib/user-notification';
import { getMemberId } from '@/shared/helpers/userCred';
import AdminChatSvg from './AdminChatSvg';
import * as styles from './styles';

import EventSourcePolyfill from 'eventsource-polyfill';

const FloatingChatroomAccess: React.FC = () => {
    console.log('FloatingChatroomAccess: Component rendering');
    const {
        chat,
        openFloatingChatroom,
        closeFloatingChatroom,
        openFloatingCareroom,
        closeFloatingCareroom,
    } = useChatContext();
    const { member } = useMemberContext();
    const location = useLocation();

    const [isLogin, setIsLogin] = useState(false);
    const [totalNewNotification, setTotalNewNotification] = useState(0);
    const [totalNewCareNotif, setTotalNewCareNotif] = useState(0);
    const [chatBoxColor, setChatBoxColor] = useState('#8D96AA');
    const newChatEventRef = useRef<EventSourcePolyfill | null>(null);
    const signalHandlerRef = useRef<((event: Event) => void) | null>(null);
    const handlingRealtimeNewMessageRef = useRef<() => void>(() => {});
    // const prevMemberIdRef = useRef<number | null>(null);
    const isSettingUpEventSourceRef = useRef<boolean>(false); // Prevent multiple simultaneous setups
    const setupTimerRef = useRef<NodeJS.Timeout | null>(null); // Track setup timer
    const abortSetupRef = useRef<boolean>(false); // Flag to abort ongoing setup

    const isNeedHangOnRight = () => {
        const pathName = location.pathname;
        const whitelistedKeywords = ['property-detail', 'project-detail'];
        return whitelistedKeywords.some(keyword => pathName.includes(keyword));
    };

    const getCurrentMemberId = useCallback((): number | null => {
        // Use the same helper as other parts of the app
        // getMemberId() from userCred.ts returns member?.sn
        if (member?.id) {
            return member.id;
        }
        const memberId = getMemberId();
        if (memberId) {
            return memberId;
        }
        console.log('FloatingChatroomAccess: No memberId found in context or localStorage');
        return null;
    }, [member]);

    const checkIfAccessTokenExist = () => {
        const token = localStorage.getItem('accessToken');
        const isLoggedIn = token !== null;
        console.log(
            'FloatingChatroomAccess: checkIfAccessTokenExist - token exists:',
            !!token,
            'isLogin will be:',
            isLoggedIn
        );
        setIsLogin(isLoggedIn);
    };

    const getTotalUserNotificationCount = useCallback(async () => {
        const memberId = getCurrentMemberId();
        if (!memberId) {
            console.log('FloatingChatroomAccess: getTotalUserNotificationCount - No memberId');
            return;
        }

        try {
            // Use placeholder [USER_ID] like public-frontend - backend will replace it
            const topicKey = 'from_user.chat_message.received_by_[USER_ID].*';
            console.log(
                'FloatingChatroomAccess: getTotalUserNotificationCount - Fetching count for topicKey:',
                topicKey
            );
            const response = await countNotifByMemberId(topicKey);
            if (response.data) {
                const newCount = response.data.total;
                console.log(
                    'FloatingChatroomAccess: getTotalUserNotificationCount - Count received:',
                    newCount
                );
                setTotalNewNotification(prevCount => {
                    console.log(
                        'FloatingChatroomAccess: getTotalUserNotificationCount - Previous count:',
                        prevCount,
                        'New count:',
                        newCount
                    );
                    return newCount;
                });
            } else {
                console.log(
                    'FloatingChatroomAccess: getTotalUserNotificationCount - No data in response'
                );
            }
        } catch (error) {
            console.error('FloatingChatroomAccess: Error fetching user notification count:', error);
        }
    }, [getCurrentMemberId]);

    const getTotalCareNotificationCount = useCallback(async () => {
        const memberId = getCurrentMemberId();
        if (!memberId) return;

        try {
            // Use placeholder [USER_ID] like public-frontend - backend will replace it
            const topicKey = 'from_admin.care_message.received_by_[USER_ID].*';
            const response = await countNotifByMemberIdWithNotNullAdminId(topicKey);
            if (response.data) {
                setTotalNewCareNotif(response.data.total);
            }
        } catch (error) {
            console.error('FloatingChatroomAccess: Error fetching care notification count:', error);
        }
    }, [getCurrentMemberId]);

    const toggleChatroomVisibility = () => {
        if (chat.isOpen) {
            closeFloatingChatroom();
        } else {
            openFloatingChatroom();
            setChatBoxColor('#8D96AA');
        }
    };

    const toggleCareroomVisibility = () => {
        if (chat.isCareRoomOpen) {
            closeFloatingCareroom();
        } else {
            openFloatingCareroom();
        }
    };

    const handlingRealtimeNewMessage = useCallback(() => {
        console.log(
            'FloatingChatroomAccess: handlingRealtimeNewMessage called - updating notification counts'
        );
        getTotalUserNotificationCount();
        getTotalCareNotificationCount();
    }, [getTotalUserNotificationCount, getTotalCareNotificationCount]);

    // Keep handler ref up-to-date
    useEffect(() => {
        handlingRealtimeNewMessageRef.current = handlingRealtimeNewMessage;
    }, [handlingRealtimeNewMessage]);

    const listenOnNewChatMessage = useCallback(() => {
        // Atomic check: Prevent multiple simultaneous setups
        if (isSettingUpEventSourceRef.current) {
            console.log('FloatingChatroomAccess: EventSource setup already in progress, skipping');
            return;
        }

        // Atomic check: Prevent multiple EventSource creation - check if already exists and is open/connecting
        if (newChatEventRef.current) {
            const readyState = newChatEventRef.current.readyState;
            console.log(
                'FloatingChatroomAccess: EventSource already exists, readyState:',
                readyState
            );

            // Only recreate if EventSource is closed (not if it's connecting or open)
            if (readyState === EventSource.CLOSED) {
                console.log(
                    'FloatingChatroomAccess: EventSource is closed, cleaning up and will recreate'
                );
                try {
                    newChatEventRef.current.removeEventListener('ping', () => {});
                    if (signalHandlerRef.current) {
                        newChatEventRef.current.removeEventListener(
                            'signal',
                            signalHandlerRef.current
                        );
                    }
                    newChatEventRef.current.close();
                } catch (e) {
                    console.log('FloatingChatroomAccess: Error cleaning up EventSource:', e);
                }
                newChatEventRef.current = null;
                signalHandlerRef.current = null;
            } else {
                // EventSource is already open or connecting, don't create another one
                console.log(
                    'FloatingChatroomAccess: EventSource already exists and is active, skipping creation'
                );
                return;
            }
        }

        // Set flag atomically - if this fails, another call is already setting up
        if (isSettingUpEventSourceRef.current) {
            console.log('FloatingChatroomAccess: Another setup started between checks, aborting');
            return;
        }

        // Mark that we're setting up - this must be the last check before creating EventSource
        isSettingUpEventSourceRef.current = true;
        abortSetupRef.current = false; // Reset abort flag

        const memberId = getCurrentMemberId();
        if (!memberId) {
            console.log('FloatingChatroomAccess: No memberId, cannot setup EventSource');
            isSettingUpEventSourceRef.current = false;
            abortSetupRef.current = false;
            return;
        }

        const token = localStorage.getItem('accessToken');
        if (!token) {
            console.log('FloatingChatroomAccess: No token, cannot setup EventSource');
            isSettingUpEventSourceRef.current = false;
            abortSetupRef.current = false;
            return;
        }

        // Check if setup was aborted
        if (abortSetupRef.current) {
            console.log('FloatingChatroomAccess: Setup was aborted, stopping');
            isSettingUpEventSourceRef.current = false;
            abortSetupRef.current = false;
            return;
        }

        const userNotificationSubs = [
            `from_user.chat_message.received_by_${memberId}.created`,
            `from_admin.care_message.received_by_${memberId}.created`,
        ];

        const topicsStr = userNotificationSubs.map(sub => `topics[]=${sub}`).join('&');

        try {
            // Final check before creating EventSource
            if (abortSetupRef.current) {
                console.log(
                    'FloatingChatroomAccess: Setup was aborted before creating EventSource'
                );
                isSettingUpEventSourceRef.current = false;
                abortSetupRef.current = false;
                return;
            }

            const url = `${process.env.REACT_APP_API_URL}/api/v2/notifications/listen?${topicsStr}`;
            console.log('FloatingChatroomAccess: Creating EventSource:', url);

            // EventSourcePolyfill uses xhrHeaders, not headers!
            const xhrHeaders = {
                Authorization: `Bearer ${token}`,
                Accept: 'text/event-stream',
                'Cache-Control': 'no-cache',
                'X-Requested-With': 'XMLHttpRequest',
            };

            const eventSource = new EventSourcePolyfill(url, {
                xhrHeaders: xhrHeaders,
                withCredentials: false,
                // EventSourcePolyfill handles reconnections automatically by default
            });

            // Check again after creating EventSource (in case it was aborted during creation)
            if (abortSetupRef.current) {
                console.log(
                    'FloatingChatroomAccess: Setup was aborted after creating EventSource, cleaning up'
                );
                try {
                    eventSource.close();
                } catch (e) {
                    // Ignore cleanup errors
                }
                isSettingUpEventSourceRef.current = false;
                abortSetupRef.current = false;
                return;
            }

            eventSource.addEventListener('ping', () => {
                console.log('FloatingChatroomAccess: EventSource ping received');
            });

            // Store handler reference for cleanup
            // Use ref to always call the latest handler (avoid stale closure)
            const signalHandler = (event: Event) => {
                const messageEvent = event as MessageEvent;
                console.log('FloatingChatroomAccess: EventSource signal received:', event);
                console.log('FloatingChatroomAccess: EventSource signal data:', messageEvent.data);
                console.log('FloatingChatroomAccess: Calling handlingRealtimeNewMessage via ref');
                // Always call the latest handler via ref
                handlingRealtimeNewMessageRef.current();
            };
            signalHandlerRef.current = signalHandler;
            eventSource.addEventListener('signal', signalHandler);

            eventSource.addEventListener('open', () => {
                // Check if this EventSource was aborted
                if (abortSetupRef.current || newChatEventRef.current !== eventSource) {
                    console.log(
                        'FloatingChatroomAccess: EventSource opened but was aborted or replaced, closing'
                    );
                    try {
                        eventSource.close();
                    } catch (e) {
                        // Ignore cleanup errors
                    }
                    return;
                }

                isSettingUpEventSourceRef.current = false;
                abortSetupRef.current = false;
            });

            eventSource.addEventListener('error', (error: any) => {
                const errorMessage = error?.message || error?.toString() || '';
                if (
                    errorMessage.includes('ECONNRESET') ||
                    errorMessage.includes('socket hang up') ||
                    errorMessage.includes('Connection closed') ||
                    errorMessage.includes('read ECONNRESET')
                ) {
                    return;
                }

                if (error.target) {
                    const target = error.target as EventSourcePolyfill;

                    if (target.readyState === EventSource.CLOSED) {
                        isSettingUpEventSourceRef.current = false;
                        abortSetupRef.current = false;
                    }
                }
            });

            if (!abortSetupRef.current) {
                newChatEventRef.current = eventSource;
            } else {
                try {
                    eventSource.close();
                } catch (e) {}
            }
        } catch (error) {
            isSettingUpEventSourceRef.current = false;
            abortSetupRef.current = false;
        }
        // Remove dependency on handlingRealtimeNewMessage - we use ref instead
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        checkIfAccessTokenExist();
    }, [member]);

    useEffect(() => {
        console.log(
            'FloatingChatroomAccess: Unified EventSource setup effect triggered. isLogin:',
            isLogin,
            'memberId:',
            getCurrentMemberId(),
            'EventSource exists:',
            !!newChatEventRef.current,
            'isSettingUp:',
            isSettingUpEventSourceRef.current
        );

        // Cleanup any existing timer
        if (setupTimerRef.current) {
            console.log('FloatingChatroomAccess: Clearing existing setup timer');
            clearTimeout(setupTimerRef.current);
            setupTimerRef.current = null;
        }

        // Abort any ongoing setup
        abortSetupRef.current = true;

        // If not logged in, ensure EventSource is closed
        if (!isLogin) {
            console.log('FloatingChatroomAccess: isLogin is false, ensuring EventSource is closed');
            if (newChatEventRef.current) {
                try {
                    newChatEventRef.current.removeEventListener('ping', () => {});
                    if (signalHandlerRef.current) {
                        newChatEventRef.current.removeEventListener(
                            'signal',
                            signalHandlerRef.current
                        );
                    }
                    newChatEventRef.current.close();
                } catch (e) {
                    console.log('FloatingChatroomAccess: Error closing EventSource:', e);
                }
                newChatEventRef.current = null;
                signalHandlerRef.current = null;
            }
            isSettingUpEventSourceRef.current = false;
            abortSetupRef.current = false;
            return;
        }

        // If already setting up or EventSource exists and is active, skip
        if (isSettingUpEventSourceRef.current) {
            console.log('FloatingChatroomAccess: EventSource setup already in progress, skipping');
            abortSetupRef.current = false; // Don't abort if already setting up
            return;
        }

        if (newChatEventRef.current) {
            const readyState = newChatEventRef.current.readyState;
            if (readyState === EventSource.OPEN || readyState === EventSource.CONNECTING) {
                console.log(
                    'FloatingChatroomAccess: EventSource already exists and is active, readyState:',
                    readyState,
                    'skipping setup'
                );
                abortSetupRef.current = false; // Don't abort if EventSource is active
                return;
            }
        }

        // Reset abort flag for new setup
        abortSetupRef.current = false;

        // Fetch counts when logged in
        getTotalUserNotificationCount();
        getTotalCareNotificationCount();

        // Setup EventSource with retry mechanism if memberId is not immediately available
        let retryCount = 0;
        const maxRetries = 10; // Try for 5 seconds (10 * 500ms)

        const trySetupEventSource = () => {
            // Check if setup was aborted
            if (abortSetupRef.current) {
                console.log('FloatingChatroomAccess: Setup was aborted, stopping retry');
                return;
            }

            // Double check conditions haven't changed
            if (!isLogin) {
                console.log('FloatingChatroomAccess: isLogin became false during setup, aborting');
                abortSetupRef.current = true;
                return;
            }

            if (isSettingUpEventSourceRef.current) {
                console.log('FloatingChatroomAccess: Setup already in progress, deferring');
                return;
            }

            if (newChatEventRef.current) {
                const readyState = newChatEventRef.current.readyState;
                if (readyState === EventSource.OPEN || readyState === EventSource.CONNECTING) {
                    console.log(
                        'FloatingChatroomAccess: EventSource already active, no need to setup'
                    );
                    return;
                }
            }

            const memberId = getCurrentMemberId();
            console.log(
                'FloatingChatroomAccess: Try setup EventSource, attempt:',
                retryCount + 1,
                'memberId:',
                memberId
            );

            if (memberId) {
                console.log(
                    'FloatingChatroomAccess: memberId available, setting up EventSource, memberId:',
                    memberId
                );
                listenOnNewChatMessage();
            } else if (retryCount < maxRetries && !abortSetupRef.current) {
                retryCount++;
                console.log(
                    'FloatingChatroomAccess: memberId not available yet, retrying in 500ms...'
                );
                setupTimerRef.current = setTimeout(trySetupEventSource, 500);
            } else {
                console.log(
                    'FloatingChatroomAccess: memberId not available after max retries or setup aborted, giving up'
                );
            }
        };

        setupTimerRef.current = setTimeout(trySetupEventSource, 500);

        return () => {
            console.log('FloatingChatroomAccess: Cleaning up setup timer');
            abortSetupRef.current = true; // Abort any ongoing setup
            if (setupTimerRef.current) {
                clearTimeout(setupTimerRef.current);
                setupTimerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLogin, member?.id]); // Dependencies: isLogin and member.id

    // Update count when chat is opened or closed (like public-frontend watch on chat.isOpen)
    // When chat is opened, user might have read messages, so refresh count
    // When chat is closed, also refresh count to show latest unread count
    useEffect(() => {
        if (isLogin) {
            // Small delay to ensure ack operations in FloatingChatroom have completed
            const timer = setTimeout(() => {
                getTotalUserNotificationCount();
                getTotalCareNotificationCount();
            }, 300);

            return () => {
                clearTimeout(timer);
            };
        }
    }, [chat.isOpen, isLogin, getTotalUserNotificationCount, getTotalCareNotificationCount]);

    // Cleanup EventSource only on component unmount (like public-frontend beforeDestroy)
    // IMPORTANT: This should only run on unmount, not when dependencies change
    useEffect(() => {
        return () => {
            console.log(
                'FloatingChatroomAccess: Component unmounting, cleaning up EventSource and timers'
            );

            // Abort any ongoing setup
            abortSetupRef.current = true;

            // Clear any pending timers
            if (setupTimerRef.current) {
                clearTimeout(setupTimerRef.current);
                setupTimerRef.current = null;
            }

            // Cleanup EventSource
            if (newChatEventRef.current) {
                try {
                    newChatEventRef.current.removeEventListener('ping', () => {});
                    // Use the stored handler reference for cleanup
                    if (signalHandlerRef.current) {
                        newChatEventRef.current.removeEventListener(
                            'signal',
                            signalHandlerRef.current
                        );
                    }
                    newChatEventRef.current.close();
                } catch (e) {
                    // Ignore cleanup errors
                }
                newChatEventRef.current = null;
                signalHandlerRef.current = null;
            }

            // Reset all flags
            isSettingUpEventSourceRef.current = false;
            abortSetupRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - only cleanup on unmount

    // Component always stays mounted (like public-frontend)
    // Only render UI when conditions are met
    // IMPORTANT: Component must stay mounted even when returning null to keep EventSource active
    if (!isLogin) {
        console.log('FloatingChatroomAccess: Returning null - isLogin:', isLogin);
        return null;
    }

    // Check if chat context exists, if not, still render (chat context should always exist)
    if (!chat) {
        console.warn('FloatingChatroomAccess: Chat context is missing, but continuing to render');
    }

    // Only show UI when chat is closed (like public-frontend v-if="isLogin && !(chat.isOpen || chat.isCareRoomOpen)")
    // But component stays mounted to keep EventSource active
    if (chat && (chat.isOpen || chat.isCareRoomOpen)) {
        console.log(
            'FloatingChatroomAccess: Chat is open, hiding UI but keeping component mounted. EventSource active:',
            !!newChatEventRef.current,
            'readyState:',
            newChatEventRef.current?.readyState
        );
        return null;
    }

    return (
        <div css={styles.wrapper}>
            <div css={[styles.floatingChatroomAccess, isNeedHangOnRight() && styles.hangOnRight]}>
                <div
                    css={styles.chatBox}
                    onClick={toggleChatroomVisibility}
                    onMouseEnter={() => setChatBoxColor('#009faa')}
                    onMouseLeave={() => setChatBoxColor('#8D96AA')}
                    style={{ color: chatBoxColor }}
                >
                    {totalNewNotification ? (
                        <span css={styles.notifSpan}>
                            <Badge count={totalNewNotification} />
                        </span>
                    ) : null}
                    <MessageOutlined style={{ fontSize: '18px' }} />
                    <h5 css={styles.chatText} style={{ color: chatBoxColor }}>
                        Chat
                    </h5>
                </div>
                <div css={styles.careBox} onClick={toggleCareroomVisibility}>
                    {totalNewCareNotif > 0 && (
                        <span css={styles.careNotifSpan}>
                            <Badge count={totalNewCareNotif} />
                        </span>
                    )}
                    <AdminChatSvg />
                </div>
            </div>
        </div>
    );
};

export default FloatingChatroomAccess;
